<template>
  <div class="credits-wrapper">
    

    <header class="page-header fade-in">
      <div>
        <h1>Crédits clients</h1>
        <p>
          {{
            hasCredits
              ? `${filteredCredits.length} clients avec des impayés`
              : "Aucun impayé enregistré"
          }}
        </p>
      </div>
      <div class="header-actions">
        <div class="search-field">
          <i class="bi bi-search"></i>
          <input
            v-model.trim="searchTerm"
            type="text"
            placeholder="Rechercher par client ou référence..."
          />
        </div>
        <button
          class="btn btn-secondary"
          @click="loadCredits"
          :disabled="loading"
        >
          <i class="bi bi-arrow-clockwise" :class="{ spinning: loading }"></i>
          <span>Actualiser</span>
        </button>
      </div>
    </header>

    <section v-if="!loading && hasCredits" class="page-body fade-in">
      <div class="summary-card">
        <div>
          <span class="label">Montant total impayé</span>
          <span class="value">{{ formatCurrency(totalUnpaidAmount) }}</span>
        </div>
      </div>

      <div class="credits-list">
        <div
          class="credit-row"
          v-for="item in filteredCredits"
          :key="item.seller_customer_reference"
        >
          <div class="credit-main">
            <h3>{{ item.customerFullName || "Client inconnu" }}</h3>
            <p>Référence client : {{ item.seller_customer_reference }}</p>
          </div>
          <div class="credit-amount">
            <span class="label">Impayé</span>
            <span class="value value--amount">
              {{ formatCurrency(item.totalUnpaid) }}
            </span>
          </div>
        </div>
      </div>
    </section>

    <div v-else-if="loading" class="loading-state">
      <div class="loader"></div>
      <p>Chargement des crédits clients...</p>
    </div>

    <div v-else class="empty-state">
      <i class="bi bi-emoji-smile"></i>
      <h3>Tout est à jour</h3>
      <p>Aucun crédit client impayé n'a été trouvé.</p>
      <button class="btn btn-primary" @click="loadCredits">
        <i class="bi bi-arrow-clockwise"></i>
        <span>Actualiser</span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "Credits",
  data() {
    return {
      loading: false,
      searchTerm: "",
    };
  },
  computed: {
    ...mapGetters("credit", {
      credits: "getCredits",
    }),
    filteredCredits() {
      const term = this.searchTerm.toLowerCase();
      if (!term) return this.credits;
      return this.credits.filter((item) => {
        const name = (item.customerFullName || "").toLowerCase();
        const reference = (item.seller_customer_reference || "").toLowerCase();
        return name.includes(term) || reference.includes(term);
      });
    },
    hasCredits() {
      return (
        Array.isArray(this.filteredCredits) && this.filteredCredits.length > 0
      );
    },
    totalUnpaidAmount() {
      if (!Array.isArray(this.filteredCredits)) return 0;
      return this.filteredCredits.reduce((sum, item) => {
        const amount = Number(item.totalUnpaid) || 0;
        return sum + amount;
      }, 0);
    },
  },
  methods: {
    goBack() {
      this.$router.back();
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined) return "—";
      const number = Number(amount);
      if (Number.isNaN(number)) return amount;
      return number.toLocaleString("fr-FR", {
        style: "currency",
        currency: "MAD",
      });
    },
    async loadCredits() {
      this.loading = true;
      try {
        await this.$store.dispatch("credit/fetchCredits");
      } catch (error) {
        console.error("Error fetching credits", error);
        this.$toast.error("Impossible de récupérer les crédits clients.");
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.loadCredits();
  },
};
</script>

<style scoped>
.credits-wrapper {
  min-height: 100vh;
  background: linear-gradient(180deg, #f7f8fb 0%, #eef1f8 100%);
  padding: 5rem 2.5rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.page-header h1 {
  margin: 0 0 0.35rem;
  font-size: 2.1rem;
  font-weight: 700;
  color: #0f172a;
}

.page-header p {
  margin: 0;
  color: #556073;
}

.header-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.search-field {
  position: relative;
  display: flex;
  align-items: center;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  padding: 0.65rem 0.9rem;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.07);
}

.search-field i {
  color: #64748b;
  margin-right: 0.6rem;
}

.search-field input {
  border: none;
  outline: none;
  font-size: 0.95rem;
  color: #1f2937;
  min-width: 240px;
}

.page-body {
  background: #ffffff;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 24px 45px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.summary-card {
  display: inline-flex;
  flex-direction: column;
  gap: 0.25rem;
  background: linear-gradient(
    135deg,
    rgba(16, 185, 129, 0.12),
    rgba(59, 130, 246, 0.1)
  );
  border-radius: 18px;
  padding: 1.5rem 2rem;
  color: #0f172a;
  max-width: 320px;
}

.summary-card .label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
}

.summary-card .value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
}

.credits-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.credit-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.5rem;
  padding: 1.25rem 1.5rem;
  border-radius: 18px;
  background: linear-gradient(
    135deg,
    rgba(37, 99, 235, 0.06),
    rgba(147, 51, 234, 0.08)
  );
  border: 1px solid rgba(148, 163, 184, 0.22);
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.06);
}

.credit-main h3 {
  margin: 0;
  font-size: 1.15rem;
  color: #0f172a;
}

.credit-main p {
  margin: 0.35rem 0 0;
  color: #64748b;
  font-size: 0.95rem;
}

.credit-amount {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
}

.credit-amount .label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
}

.credit-amount .value {
  font-size: 1.2rem;
  font-weight: 700;
  color: #0f172a;
}

.value--amount {
  color: #0f766e;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 50vh;
  color: #6b7280;
  text-align: center;
}

.empty-state i {
  font-size: 3rem;
  color: #22c55e;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.1rem;
  border-radius: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn:hover {
  transform: translateY(-1px);
}

.btn-light {
  background: rgba(148, 163, 184, 0.15);
  color: #1f2937;
}

.back-button-global {
  position: fixed;
  top: 1.25rem;
  left: 1.25rem;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.35);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.18);
  z-index: 3000;
}

.btn-secondary {
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
}

.btn-primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
  box-shadow: 0 10px 22px rgba(79, 70, 229, 0.25);
}

.loader {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 4px solid #d1d5db;
  border-top-color: #2563eb;
  animation: spin 0.8s linear infinite;
}

.bi-arrow-clockwise.spinning {
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1024px) {
  .credits-wrapper {
    padding: 1.5rem 1.5rem 4rem;
  }
  .page-body {
    padding: 1.5rem;
  }
}

@media (max-width: 768px) {
  .credits-wrapper {
    padding: 4.5rem 1rem 4rem;
  }
  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }
  .search-field input {
    min-width: unset;
  }
  .credit-row {
    flex-direction: column;
    align-items: flex-start;
  }
  .credit-amount {
    align-items: flex-start;
  }
  .back-button-global {
    top: 0.75rem;
    left: 0.75rem;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16);
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.75rem;
  }
  .summary-card {
    max-width: 100%;
    padding: 1.25rem;
  }
}
</style>
